import {useQueryClient} from '@tanstack/react-query';
import {useFlags} from 'launchdarkly-react-client-sdk';
import React, {useCallback, useMemo} from 'react';
import {useSearchParams} from 'react-router-dom';
import {PoppaysLoader} from '@lightricks/react-design-system';
import CAMPAIGN_STATE from '@/config/campaignState';
import CampaignsViewSwitcher from '@/components/campaigns-view-switcher';
import G2ReviewModal from '@/components/g2-review-modal/G2ReviewModal';
import Page from '@/components/page';
import useCreateG2ReviewSession from '@/hooks/mutations/use-create-g2-review-session';
import useCampaignsV2ListQuery, {
  CampaignListItem,
} from '@/hooks/queries/use-campaigns-v2-list-query/useCampaignsV2ListQuery';
import useG2ReviewStatus from '@/hooks/queries/use-g2-review-status';
import useBrandId from '@/hooks/use-brand-id';
import useScrollPosition from '@/hooks/use-scroll-position';
import styles from './CampaignsList.module.scss';
import CampaignCardList from './components/campaign-card-list/CampaignCardList';
import CampaignStatusTabs from './components/campaign-status-tabs/CampaignStatusTabs';
import CampaignsListHeader from './components/campaigns-list-header/CampaignsListHeader';
import type {TabFilter} from './constants';
import {TAB_FILTER} from './constants';

const TAB_STATE_MAP: Record<TabFilter, string[]> = {
  [TAB_FILTER.All]: [],
  [TAB_FILTER.Live]: [
    CAMPAIGN_STATE.ACCEPTING_PROPOSALS,
    CAMPAIGN_STATE.CLOSED_TO_NEW_PROPOSALS,
  ],
  [TAB_FILTER.Drafts]: [
    CAMPAIGN_STATE.DRAFT,
    CAMPAIGN_STATE.UNDER_CONSTRUCTION,
    CAMPAIGN_STATE.UNDER_REVIEW,
    CAMPAIGN_STATE.APPROVED,
  ],
  [TAB_FILTER.Completed]: [CAMPAIGN_STATE.CLOSED],
};

const VALID_TABS = new Set<string>(Object.values(TAB_FILTER));

function isValidTab(value: string | null): value is TabFilter {
  return value !== null && VALID_TABS.has(value);
}

// Lower number = appears earlier in the All tab list.
// Unknown / unrecognized states fall to the end (group 3).
function getGroupOrder(state: string): number {
  if (TAB_STATE_MAP[TAB_FILTER.Live].includes(state)) {
    return 0;
  }
  if (TAB_STATE_MAP[TAB_FILTER.Drafts].includes(state)) {
    return 1;
  }
  if (TAB_STATE_MAP[TAB_FILTER.Completed].includes(state)) {
    return 2;
  }
  return 3;
}

// Safe createdAt timestamp; campaigns with missing/invalid dates sort last.
function getCreatedAtTime(campaign: CampaignListItem): number {
  const time = new Date(campaign.createdAt).getTime();
  return Number.isFinite(time) ? time : 0;
}

function compareByCreatedAtDesc(
  a: CampaignListItem,
  b: CampaignListItem
): number {
  return getCreatedAtTime(b) - getCreatedAtTime(a);
}

export default function CampaignsList() {
  const brandId = useBrandId();
  const queryClient = useQueryClient();
  const {g2Integration} = useFlags();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    data: g2ReviewSession,
    mutate: createG2ReviewSession,
    reset: resetG2ReviewSession,
    status: g2ReviewSessionStatus,
  } = useCreateG2ReviewSession();
  const {
    data: g2ReviewStatus,
    isFetched: g2ReviewStatusFetched,
    isError: g2ReviewStatusFailed,
  } = useG2ReviewStatus(g2Integration);
  const tabParam = searchParams.get('tab');
  const activeTab: TabFilter = isValidTab(tabParam) ? tabParam : TAB_FILTER.All;

  const setActiveTab = useCallback(
    (tab: TabFilter) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', tab);
          return next;
        },
        {replace: true}
      );
    },
    [setSearchParams]
  );

  // Fetch all campaigns (no filter) to compute tab counts
  const scrollRef = useScrollPosition<HTMLDivElement>(
    `campaigns-list-${brandId}`
  );

  const {campaigns, isLoading} = useCampaignsV2ListQuery({
    brandId,
    states: [],
    refetchOnWindowFocus: false,
  });

  // Group campaigns by state for tab counts
  const tabCounts = useMemo(() => {
    return {
      all: campaigns.length,
      live: campaigns.filter((c: CampaignListItem) =>
        TAB_STATE_MAP.live.includes(c.state)
      ).length,
      drafts: campaigns.filter((c: CampaignListItem) =>
        TAB_STATE_MAP.drafts.includes(c.state)
      ).length,
      completed: campaigns.filter((c: CampaignListItem) =>
        TAB_STATE_MAP.completed.includes(c.state)
      ).length,
    };
  }, [campaigns]);

  // Filter + sort campaigns based on active tab.
  // - All tab: group by Live -> Drafts -> Completed -> Other, with each group
  //   sorted by createdAt descending (newest first).
  // - Other tabs: filter to the tab's states, then sort by createdAt desc.
  const filteredCampaigns = useMemo(() => {
    if (activeTab === TAB_FILTER.All) {
      return [...campaigns].sort((a, b) => {
        const groupDiff = getGroupOrder(a.state) - getGroupOrder(b.state);
        if (groupDiff !== 0) {
          return groupDiff;
        }
        return compareByCreatedAtDesc(a, b);
      });
    }
    const statesToInclude = TAB_STATE_MAP[activeTab];
    return campaigns
      .filter((c: CampaignListItem) => statesToInclude.includes(c.state))
      .sort(compareByCreatedAtDesc);
  }, [campaigns, activeTab]);

  const openG2ReviewModal = useCallback(() => {
    queryClient.invalidateQueries({queryKey: ['campaignsV2List']});

    if (
      !g2Integration ||
      !g2ReviewStatusFetched ||
      g2ReviewStatusFailed ||
      g2ReviewStatus?.reviewExists
    ) {
      return;
    }

    createG2ReviewSession();
  }, [
    createG2ReviewSession,
    g2Integration,
    g2ReviewStatus?.reviewExists,
    g2ReviewStatusFailed,
    g2ReviewStatusFetched,
    queryClient,
  ]);

  const closeG2ReviewModal = useCallback(() => {
    resetG2ReviewSession();
    queryClient.invalidateQueries({queryKey: ['campaignsV2List']});
    queryClient.invalidateQueries({queryKey: ['g2ReviewStatus']});
  }, [queryClient, resetG2ReviewSession]);

  return (
    <>
      {isLoading ? (
        <div className={styles.container}>
          <PoppaysLoader />
        </div>
      ) : (
        <Page
          headerComponent={<CampaignsListHeader />}
          className={styles.container}
          childrenClassName={styles.contentWrapper}
          data-testid="campaigns-list"
        >
          <div className={styles.content} ref={scrollRef}>
            <div className={styles.contentInner}>
              <CampaignStatusTabs
                activeTab={activeTab}
                onTabChange={setActiveTab}
                counts={tabCounts}
                rightSlot={<CampaignsViewSwitcher />}
              />
              <CampaignCardList
                campaigns={filteredCampaigns}
                onCloseFeedbackSubmitted={openG2ReviewModal}
              />
            </div>
          </div>
        </Page>
      )}
      <G2ReviewModal
        open={g2Integration && g2ReviewSessionStatus !== 'idle'}
        reviewUrl={g2ReviewSession?.reviewUrl}
        status={g2ReviewSessionStatus}
        onClose={closeG2ReviewModal}
        onRetry={createG2ReviewSession}
      />
    </>
  );
}
